import React, { useEffect, useRef, useState } from 'react';
import { fmtFecha } from '../../constants';
import { CredencialEmpleado } from './CredencialEmpleado';
import { calcularAntiguedad } from './antiguedad';
import { CambiosMobile, CapacitacionesMobile, DocumentosMobile } from './SeccionesExpediente';
import {
  obtenerDetalleEmpleado, obtenerCapacitacionesEmpleado, obtenerAnexosEmpleado, mismoIdEmpleado
} from './apiExpediente';

// Expediente Mobile consultivo (M.2B). Se monta con key = id seleccionado:
// al cambiar de empleado todo el estado (detalle, capacitaciones, anexos)
// se descarta, así nunca se ve un instante el detalle del anterior.
// Sólo lectura: ninguna acción de escritura de Desktop se traslada aquí.

export const SECCIONES_EXPEDIENTE = [
  { id: 'cambios', titulo: 'Cambios', icono: 'ti-arrows-exchange', descripcion: 'Historial de movimientos' },
  { id: 'capacitaciones', titulo: 'Capacitaciones', icono: 'ti-school', descripcion: 'Cursos asociados' },
  { id: 'documentos', titulo: 'Documentos', icono: 'ti-file-description', descripcion: 'Documentos firmados y registros' }
];

// Carga bajo demanda: pide sólo cuando `activo` y una sola vez por intento
// exitoso (volver a la sección no repite la petición). `reintentar` fuerza
// una nueva petición. Respuestas tardías de un intento anterior se ignoran.
export function useRecurso(cargar, activo) {
  const [recurso, setRecurso] = useState({ estado: 'inactivo', datos: null });
  const [intento, setIntento] = useState(0);
  const cargado = useRef(null);
  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;

  useEffect(() => {
    if (!activo || cargado.current === intento) return undefined;
    let vigente = true;
    setRecurso({ estado: 'cargando', datos: null });
    cargarRef.current().then(
      datos => { if (vigente) { cargado.current = intento; setRecurso({ estado: 'listo', datos }); } },
      () => { if (vigente) setRecurso({ estado: 'error', datos: null }); }
    );
    return () => { vigente = false; };
  }, [activo, intento]);

  return [recurso, () => setIntento(i => i + 1)];
}

// Teléfono legible (llega como número o texto desde la hoja). Sólo formato.
function presentarTelefono(valor) {
  const texto = String(valor == null ? '' : valor).trim();
  const digitos = texto.replace(/\D/g, '');
  if (!digitos) return null;
  let legible = texto;
  if (digitos.length === 10) {
    legible = /^(33|55|56|81)/.test(digitos)
      ? `${digitos.slice(0, 2)} ${digitos.slice(2, 6)} ${digitos.slice(6)}`
      : `${digitos.slice(0, 3)} ${digitos.slice(3, 6)} ${digitos.slice(6)}`;
  }
  return { legible, href: `tel:${digitos}` };
}

// Información rápida: sólo propiedades reales de la lista / api/empleado/detalle.
// Fecha de ingreso sale de la lista (yyyy-MM-dd, igual que Desktop); el detalle
// la trae como fecha ISO UTC y sólo se usa si la lista no la tiene. La
// antigüedad se calcula aquí (no existe en el backend) y se omite si la fecha
// falta o es inválida.
export function datosRapidos(empleado, detalle, hoy = new Date()) {
  const d = detalle || {};
  const fecha = String(empleado.fecha_ingreso || '').trim() || String(d.fecha_ingreso || '').trim();
  const antiguedad = calcularAntiguedad(fecha, hoy);
  const texto = v => String(v == null ? '' : v).trim();
  const datos = [
    fecha && { id: 'ingreso', etiqueta: 'Ingreso', valor: fmtFecha(fecha) },
    antiguedad && { id: 'antiguedad', etiqueta: 'Antigüedad', valor: antiguedad },
    texto(d.tipo_empleado) && { id: 'tipo-empleado', etiqueta: 'Empleado', valor: texto(d.tipo_empleado) },
    texto(d.tipo_contrato) && { id: 'contrato', etiqueta: 'Contrato', valor: texto(d.tipo_contrato) }
  ].filter(Boolean);
  return { datos, telefono: presentarTelefono(d.telefono) };
}

function ErrorCarga({ texto, onReintentar }) {
  return (
    <div className="m-exp-error" role="alert">
      <i className="ti ti-cloud-off" aria-hidden="true"></i>
      <span>{texto}</span>
      <button type="button" className="m-exp-reintentar" onClick={onReintentar}>Reintentar</button>
    </div>
  );
}

function InformacionRapida({ empleado, detalle, onReintentar }) {
  const titulo = <h2 id="m-exp-rapida" className="m-exp-titulo">Información rápida</h2>;
  if (detalle.estado === 'error') {
    return (
      <section className="m-exp-bloque" aria-labelledby="m-exp-rapida">
        {titulo}
        <ErrorCarga texto="No se pudo cargar la información del expediente." onReintentar={onReintentar} />
      </section>
    );
  }
  if (detalle.estado !== 'listo') {
    return (
      <section className="m-exp-bloque" aria-labelledby="m-exp-rapida" aria-busy="true">
        {titulo}
        <div className="m-rapida m-rapida-cargando" role="status" aria-label="Cargando información del expediente">
          <div className="m-rapida-datos">
            {[0, 1, 2, 3].map(i => <div key={i} className="m-rapida-dato"><span></span><span></span></div>)}
          </div>
        </div>
      </section>
    );
  }
  const { datos, telefono } = datosRapidos(empleado, detalle.datos);
  if (datos.length === 0 && !telefono) return null;
  return (
    <section className="m-exp-bloque" aria-labelledby="m-exp-rapida">
      {titulo}
      <div className="m-rapida">
        {datos.length > 0 && (
          <dl className="m-rapida-datos">
            {datos.map(f => (
              <div key={f.id} className="m-rapida-dato"><dt>{f.etiqueta}</dt><dd>{f.valor}</dd></div>
            ))}
          </dl>
        )}
        {telefono && (
          <a className="m-rapida-tel" href={telefono.href} aria-label={`Llamar al ${telefono.legible}`}>
            <i className="ti ti-phone" aria-hidden="true"></i>
            <span className="m-rapida-tel-numero">{telefono.legible}</span>
            <span className="m-rapida-llamar" aria-hidden="true">Llamar</span>
          </a>
        )}
      </div>
    </section>
  );
}

export function ExpedienteMobile({ empleado, id, cambios, seccion, onAbrirSeccion, onVolver, guardiaSalida }) {
  const [detalle, reintentarDetalle] = useRecurso(() => obtenerDetalleEmpleado(id).then(emp => {
    // El detalle debe corresponder al ID abierto.
    if (!emp || !mismoIdEmpleado(emp.id, id)) throw new Error('Detalle de otro empleado');
    return emp;
  }), true);
  const [capacitaciones, reintentarCapacitaciones] = useRecurso(() => obtenerCapacitacionesEmpleado(id), seccion === 'capacitaciones');
  const [anexos, reintentarAnexos] = useRecurso(() => obtenerAnexosEmpleado(id), seccion === 'documentos');

  const cambiosEmpleado = (cambios || []).filter(c => mismoIdEmpleado(c.id_empleado, id));
  const datosDetalle = detalle.estado === 'listo' ? detalle.datos : null;
  const nombre = String(empleado?.nombre || '').trim();

  if (!empleado) {
    return (
      <section className="m-cred-vista">
        <button type="button" className="m-volver" onClick={onVolver}>
          <i className="ti ti-arrow-left" aria-hidden="true"></i> Empleados
        </button>
        <div className="m-emp-vacio" role="status">
          <i className="ti ti-user-off" aria-hidden="true"></i>
          <p className="m-emp-vacio-titulo">Empleado no disponible</p>
          <p className="m-emp-vacio-texto">Ya no aparece en la lista actual.</p>
        </div>
      </section>
    );
  }

  if (seccion) {
    const contexto = [nombre.toUpperCase(), empleado.id != null && `#${String(empleado.id).trim()}`].filter(Boolean).join(' · ');
    const titulo = SECCIONES_EXPEDIENTE.find(s => s.id === seccion)?.titulo || '';
    return (
      <section className="m-exp-seccion" aria-labelledby="m-exp-seccion-titulo">
        <button type="button" className="m-volver" onClick={onVolver}>
          <i className="ti ti-arrow-left" aria-hidden="true"></i> Expediente
        </button>
        <h1 id="m-exp-seccion-titulo" className="m-titulo-pagina m-exp-seccion-titulo">{titulo}</h1>
        <p className="m-exp-contexto">{contexto}</p>
        {seccion === 'cambios' && <CambiosMobile cambios={cambiosEmpleado} />}
        {seccion === 'capacitaciones' && (
          <CapacitacionesMobile
            recurso={capacitaciones}
            onReintentar={reintentarCapacitaciones}
            ErrorCarga={ErrorCarga}
            idEmpleado={empleado.id != null ? String(empleado.id).trim() : id}
            nombreEmpleado={nombre || `#${id}`}
            guardiaSalida={guardiaSalida}
          />
        )}
        {seccion === 'documentos' && (
          <DocumentosMobile
            anexos={anexos}
            onReintentar={reintentarAnexos}
            detalle={datosDetalle}
            ErrorCarga={ErrorCarga}
            idEmpleado={empleado.id != null ? String(empleado.id).trim() : id}
            nombreEmpleado={nombre || `#${id}`}
            guardiaSalida={guardiaSalida}
          />
        )}
      </section>
    );
  }

  const foto = datosDetalle && /^https:\/\//.test(String(datosDetalle.foto_url || '')) ? datosDetalle.foto_url : null;
  const resumenCambios = cambiosEmpleado.length === 0
    ? 'Sin movimientos registrados'
    : `${cambiosEmpleado.length} ${cambiosEmpleado.length === 1 ? 'movimiento' : 'movimientos'}`;

  return (
    <section className="m-cred-vista">
      <button type="button" className="m-volver" onClick={onVolver}>
        <i className="ti ti-arrow-left" aria-hidden="true"></i> Empleados
      </button>
      <CredencialEmpleado empleado={empleado} foto={foto} />
      <InformacionRapida empleado={empleado} detalle={detalle} onReintentar={reintentarDetalle} />

      <section className="m-exp-bloque" aria-labelledby="m-exp-secciones">
        <h2 id="m-exp-secciones" className="m-exp-titulo">Expediente</h2>
        <ul className="m-exp-lista">
          {SECCIONES_EXPEDIENTE.map(s => (
            <li key={s.id}>
              <button type="button" className="m-exp-entrada" onClick={() => onAbrirSeccion(s.id)}>
                <span className="m-exp-entrada-icono" aria-hidden="true"><i className={`ti ${s.icono}`}></i></span>
                <span className="m-exp-entrada-texto">
                  <span className="m-exp-entrada-titulo">{s.titulo}</span>
                  <span className="m-exp-entrada-desc">{s.id === 'cambios' ? resumenCambios : s.descripcion}</span>
                </span>
                <i className="ti ti-chevron-right m-emp-chev" aria-hidden="true"></i>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
